import React from 'react';
import { observer } from 'mobx-react';

import { Button } from 'components/Button';

import MainHeader from 'components/MainHeader';

import { useStore } from 'utils/hooks/store';

import API from 'utils/api';

import { useTimeZone } from 'utils/formatters/time';
import { exportTimeZoneParams, formatMoment } from 'utils/formatters/timezone';

import AlarmStatusChip from 'components/Chips/AlarmStatusChip';

import { AlarmDetailsResponse } from 'utils/api/modules/alarms/alarms.types';
import AlarmSource from 'components/AlarmSource';

import routes from 'core/routes';
import { DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR } from 'core/constants';

import UserRow from 'components/UserRow';

import AlarmActions from '../AlarmActions';

import { MainInformation } from './styles';

const AlarmDetailsHeader: React.FC = (): React.ReactElement => {
  const {
    alarmDetails: { details: detailsRaw },
  } = useStore();

  const details = detailsRaw as AlarmDetailsResponse;

  const tz = useTimeZone();

  const handleExportAlarm = React.useCallback(async (): Promise<void> => {
    if (!details) {
      return;
    }
    await API.alarms.exportAlarm(details.uuid, exportTimeZoneParams(tz))();
  }, [details, tz]);

  if (details === undefined) {
    return <></>;
  }

  return (
    <>
      <MainHeader
        title={details.subject}
        breadcrumbs={[
          {
            label: 'Lone Working',
            link: String(routes.dashboard.alarms.red),
          },
          {
            label: 'Overview',
            link: String(routes.dashboard.alarms.red),
          },
          {
            label: 'Alarm',
            link: 'Alarms',
          },
        ]}
        action={
          <Button onClick={handleExportAlarm} variant="contained" size="large" data-testid="alarm-export-button">
            Export
          </Button>
        }
      />
      <AlarmActions />
      <MainInformation>
        <div>
          <h1>Name</h1>
          <p id="alarm-subject">{details.subject}</p>
        </div>

        <div>
          <h1>Started by</h1>
          <UserRow user={{ uuid: details.user.uuid, name: details.user.name }} />
        </div>

        <div>
          <h1>Status</h1>
          <AlarmStatusChip status={details.alertStatus} />
        </div>

        <div>
          <h1>Raised at</h1>
          <p data-testid="alarm-header-raised-at">
            {formatMoment(details.activated, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR)}
          </p>
        </div>

        <div>
          <h1>Source</h1>
          {details.device ? <AlarmSource device={details.device} /> : <p>N/A</p>}
        </div>
      </MainInformation>
    </>
  );
};

export default observer(AlarmDetailsHeader);
